<!--
  - Copyright (c) 2022-2024. Haodong JU
  -->

<template>
  <div>
    <ik-data-table
      ref="billTable"
      :custom-on-row-click="goNext"
      :icon="'mdi-format-list-bulleted'"
      :model="model"
      :required-date-value="$parent.dateRange"
      :use-default-action="false"
      :use-select="false"
      entity-name="allBills"
      style="width: 100%"
    >
      <template
        #item.printFrenchBill="{item}"
      >
        <v-btn
          :height="!$vuetify.breakpoint.lgAndUp ? '40px' : ''"
          :icon="!$vuetify.breakpoint.lgAndUp"
          :style="!$vuetify.breakpoint.lgAndUp ? 'border-radius: 12px; background: #f57c00;' : ''"
          :width="!$vuetify.breakpoint.lgAndUp ? '40px' : ''"
          color="amber lighten-4 black--text"
          elevation="0"
          small
          @click.stop="showPrintDialog(item)"
        >
          <template v-if="!$vuetify.breakpoint.lgAndUp">
            <v-icon color="white">
              mdi-cog
            </v-icon>
          </template>
          <template v-if="$vuetify.breakpoint.lgAndUp">
            <div
              :class="!$vuetify.breakpoint.lgAndUp ? 'text-truncate' : ''"
              :style="!$vuetify.breakpoint.lgAndUp ? 'max-width: 100px;' : ''"
            >
              {{ $t('PrintMenu') }}
            </div>
          </template>
        </v-btn>
      </template>
      <template
        #item.printItalyBill="{item}"
      >
        <v-btn
          :height="!$vuetify.breakpoint.lgAndUp ? '40px' : ''"
          :icon="!$vuetify.breakpoint.lgAndUp"
          :style="!$vuetify.breakpoint.lgAndUp ? 'border-radius: 12px; background: #f57c00;' : ''"
          :width="!$vuetify.breakpoint.lgAndUp ? '40px' : ''"
          color="amber lighten-4 black--text"
          elevation="0"
          small
          @click.stop="openXMLDialog(item)"
        >
          <template v-if="!$vuetify.breakpoint.lgAndUp">
            <v-icon color="white">
              mdi-cog
            </v-icon>
          </template>
          <template v-if="$vuetify.breakpoint.lgAndUp">
            <div
              :class="!$vuetify.breakpoint.lgAndUp ? 'text-truncate' : ''"
              :style="!$vuetify.breakpoint.lgAndUp ? 'max-width: 100px;' : ''"
            >
              {{ $t('createBill') }}
            </div>
          </template>
        </v-btn>
      </template>
      <template #item.tableName="{item}">
        {{ item.tableName+(item.isTakeaway?'/Cloud':'') }}
      </template>
      <template #item.totalPrice="{item}">
        <template v-if="item.consumeTypeStatusId==='6'">
          <price-table-display :price="item.totalPrice" />
        </template>
        <template v-else-if="item.consumeTypeStatusId==='5'">
          <div class="text-decoration-line-through">
            <price-table-display
              :price="item.totalPrice"
              color="red"
            />
          </div>
        </template>
        <template v-else>
          <price-table-display
            :price="item.totalPrice"
            color="yellow"
          />
        </template>
      </template>
      <template #footer="{tableItems}">
        <v-card
          :class="!$vuetify.breakpoint.lgAndUp ? 'px-4' : 'px-6'"
          class="py-2 mr-4 text-body-2"
          color="grey lighten-3 black--text"
          elevation="0"
        >
          {{ $t('TotalAmount') }} {{ calculateTotalPaymentPrice(tableItems).toFixed(2) | priceDisplay }}
        </v-card>
      </template>
    </ik-data-table>
    <v-dialog
      v-model="activeDialog"
      max-width="500px"
      persistent
    >
      <base-form
        :schema="schemas"
        @close="activeDialog=false"
        @submit="savePrintInfo"
      >
        <div>{{ $t('PrintMenus') }}</div>
        <template #subtitle>
          {{ $t('PrintMenusSubtitle') }}
        </template>
      </base-form>
    </v-dialog>
    <v-dialog
      v-model="createXMLDialog"
      max-width="800px"
      persistent
    >
      <base-form
        v-model="holderObject"
        :loading="loading"
        :need-jump-btn="true"
        :schema="ItalySchemas"
        @close="createXMLDialog=false"
        @submit="createXML"
        @submitAndJump="submitAndJump"
        @holder-change="onHolderChange"
      >
        <div>{{ $t('PrintMenus') }}</div>
        <template #subtitle>
          {{ $t('PrintMenusSubtitle') }}
        </template>
      </base-form>
    </v-dialog>
  </div>
</template>

<script>

import HistoryBills, {
  isAddAndPrintV2,
  printFrenchBillController
} from '../../../../../model/dataLayer/api/bills/HistoryBills'
import IkDataTable from '@/plugins/EntityFrameWork/IkDataTable.vue'
import { mapMutations, mapState } from 'vuex'
import BaseForm from '@/views/widget/INK/BaseForm/BaseForm'
import PriceTableDisplay from '@/views/widget/typography/PriceTableDisplay.vue'
import DateDayPicker from '@/views/widget/commonLogic/DateDayPicker.vue'
import { createFPA, getFPAListByOrderId } from '@/model/dataLayer/api/Italy/ItalyBills'
import i18n from '@/i18n'
import { showSuccessMessage } from '@/model/utils/utils'
import { today } from '@/model/utils/DateRepository'
import { getBillInfoByNumber } from '@/model/dataLayer/api/Italy/ItalyXML'

export default {
  name: 'HistoryBillsPage',
  components: {
    PriceTableDisplay,
    BaseForm,
    IkDataTable
  },
  data: function () {
    return ({
      loading: false,
      holderObject: {},
      createXMLDialog: false,
      showPrintOption: '',
      activeDialog: false,
      collapsedPrice: false,
      model: HistoryBills,
      orderId: '',
      selectedOrder: null,
      newBackendVersion: false
    })
  },
  computed: {
    ...mapState(['showBillDetailDialog']),
    schemas () {
      return [
        {
          key: 'personCount',
          name: 'PersonCount'
        },
        {
          key: 'targetAmount',
          name: 'totalAmount',
          hint: this.newBackendVersion ? '' : i18n.t('callUsUpdateBackend'),
          default: this.selectedOrder ? this.selectedOrder.totalPrice : 0,
          componentProps: {
            disabled: !this.newBackendVersion,
            rules: [v => {
              if (this.selectedOrder && parseFloat(v) > parseFloat(this.selectedOrder.totalPrice)) {
                return i18n.t('mustNotExceed') + this.selectedOrder.totalPrice
              }
              return true
            }]
          }
        },
        {
          key: 'note',
          name: 'note'
        }
      ]
    },
    ItalySchemas () {
      return [
        {
          key: 'date',
          name: 'Data documento',
          component: DateDayPicker,
          default: today,
          componentProps: {
            locale: i18n.locale
          }
        },
        {
          key: 'pecDestinatario',
          name: 'Destinatario PEC',
          required: false,
          default: '',
          componentProps: {
            rules: [v => {
              if (!v) {
                v = ''
              }
              if (v !== '') {
                return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(v) || i18n.t('EnterEmailAddress')
              } else {
                return true
              }
            }]
          }
        },
        {
          key: 'pivaPaese',
          name: 'P.IVA Paese',
          componentProps: {
            rules: [v => /^[A-Z]{2}$/.test(v) || '[A-Z]{2}']
          }
        },
        {
          key: 'piva',
          name: 'P.IVA',
          componentProps: {
            rules: [v => /^.{1,28}$/.test(v) || '1-28'],
            iconAppend: {
              icon: 'mdi-magnify',
              onClick: async () => {
                this.loading = true
                if (this.holderObject.pivaPaese && this.holderObject.piva) {
                  const res = await getBillInfoByNumber(this.holderObject.pivaPaese, this.holderObject.piva)
                  if (res.isValid) {
                    const addressInfo = res.address.split('\n')
                    this.holderObject.denominazione = res.name
                    this.holderObject.indirizzo = addressInfo[0].trim()
                    const cityInfo = addressInfo[1].trim().split(' ')
                    this.holderObject.cap = cityInfo[0]
                    this.holderObject.comune = cityInfo[1]
                    this.holderObject.provincia = cityInfo[2]
                    this.holderObject.nazione = this.holderObject.pivaPaese
                  } else {
                    if (res.userError === 'MS_MAX_CONCURRENT_REQ') {
                      this.showErrorDialog(i18n.t('waitAndTry'))
                    } else {
                      this.showErrorDialog(res.userError)
                    }
                  }
                } else {
                  let errorMessage = i18n.t('pleaseInput')
                  errorMessage += this.holderObject.pivaPaese ? '' : 'P.IVA Pease,'
                  errorMessage += this.holderObject.piva ? '' : 'P.IVA'
                  this.showErrorDialog(errorMessage)
                }
                this.loading = false
              }
            }
          }
        },
        {
          key: 'codiceDestinatario',
          name: 'Codice Amministrazione destinataria',
          default: '0000000',
          componentProps: {
            rules: [v => /^[A-Z0-9]{6,7}$/.test(v) || '[A-Z0-9]{6,7}']
          }
        },
        {
          key: 'denominazione',
          name: 'Denominazione',
          componentProps: {
            rules: [v => /^[\x20-\xFF]{1,80}$/.test(v) || '1-80']
          }
        },
        {
          key: 'indirizzo',
          name: 'Indirizzo',
          componentProps: {
            rules: [v => /^[\x20-\xFF]{1,60}$/.test(v) || '1-60']
          }
        },
        {
          key: 'cap',
          name: 'CAP',
          componentProps: {
            rules: [v => /^[0-9]{5}$/.test(v) || '[0-9]{5}']
          }
        },
        {
          key: 'comune',
          name: 'Comune',
          componentProps: {
            rules: [v => /^[\x20-\xFF]{1,60}$/.test(v) || '1-60']
          }
        },
        {
          key: 'provincia',
          name: 'Provincia',
          componentProps: {
            rules: [v => /^[A-Z]{2}$/.test(v) || '[A-Z]{2}']
          }
        },
        {
          key: 'nazione',
          name: 'Nazione',
          componentProps: {
            rules: [v => /^[A-Z]{2}$/.test(v) || '[A-Z]{2}']
          }
        },
        {
          key: 'codiceFiscale',
          name: 'Codice Fiscale',
          required: false,
          default: '',
          componentProps: {
            rules: [v => {
              if (v !== '') {
                return /^[A-Z0-9]{11,16}$/.test(v) || '[A-Z0-9]{11,16}'
              } else {
                return true
              }
            }]
          }
        },
        {
          key: 'causale',
          name: 'Causale',
          required: false,
          default: '',
          componentProps: {
            rules: [v => /^[\x20-\xFF]*$/.test(v) || '']
          }
        }
      ]
    }
  },
  watch: {
    '$parent.dateRange': {
      handler: function (val) {

      },
      deep: true,
      immediate: true
    },
    showBillDetailDialog (val) {
      if (!val) {
        this.reloadData()
      }
    },
    createXMLDialog (val) {
      if (!val) {
        this.$refs.billTable.reload()
      }
    }
  },
  async mounted () {},
  methods: {
    ...mapMutations(['showErrorDialog']),
    onHolderChange (newHolderObject) {
      this.holderObject = newHolderObject
    },
    async openXMLDialog (item) {
      const res = await getFPAListByOrderId(item.id) ?? []
      if (res.length > 0) {
        this.showErrorDialog(i18n.t('alreadyCreateXML'))
      } else {
        this.orderId = item.id
        this.createXMLDialog = true
      }
    },
    async submitAndJump (info) {
      info.orderId = this.orderId
      await createFPA(info)
      showSuccessMessage(i18n.t('successful'))
      this.createXMLDialog = false
      await this.$router.push({
        path: '/billAndReport/ItalyXML',
        query: { orderId: this.orderId }
      })
    },
    async createXML (info) {
      info.orderId = this.orderId
      await createFPA(info)
      showSuccessMessage(i18n.t('successful'))
      this.createXMLDialog = false
      await this.$refs.billTable.reload()
    },
    async showPrintDialog (item) {
      this.orderId = item.id
      this.selectedOrder = item
      this.newBackendVersion = await isAddAndPrintV2()
      this.activeDialog = true
    },
    async savePrintInfo (info) {
      info.orderId = this.orderId
      await printFrenchBillController(info)
      this.activeDialog = false
    },
    calculateTotalPaymentPrice (items) {
      if (!this.$refs.billTable || !this.$refs.billTable.filterItem.paymentArr ||
          (this.$refs.billTable.filterItem.paymentArr.length === 0)) {
        return items?.reduce(
          (arr, item) =>
            parseInt(item.consumeTypeStatusId) === 6 ? arr + parseFloat(item.totalPrice) : arr
          , 0) ?? 0
      } else {
        const paymentArr = this.$refs.billTable.filterItem.paymentArr
        let totalMoney = 0
        paymentArr.forEach(indexItem => {
          for (const item of items) {
            if (item.payLogs !== null) {
              item.payLogs.forEach(payItem => {
                if (parseInt(payItem.paymentMethodId) === parseInt(indexItem)) {
                  totalMoney += parseFloat(payItem.amount)
                }
              })
            }
          }
        })
        return totalMoney
      }
    },
    ...mapMutations(['showBillDetail']),
    goNext (item) {
      this.showBillDetail(item.orderId)
    },
    reloadData () {
      this.showDetail = false
      this.$refs.billTable.reload()
    }

  }
}
</script>
